import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { crmService } from '../../services/crm.service';
import { crmErrorMessage, formatAddress, leadTypeLabel, propertyOrAreasSummary } from '../../utils/crm';
import {
  CHUNK_SIZE,
  MAX_ROWS,
  chunk,
  prepareImport,
  readSpreadsheet,
  reportCsv,
  templateCsv,
} from '../../utils/crmImport';
import './CrmImportDialog.css';

const download = (filename, text) => {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const displayName = (request) => [request.firstName, request.lastName].filter(Boolean).join(' ')
  || request.emails?.[0]?.value || request.phones?.[0]?.value || 'Unnamed';

/** What the preview shows for the property or areas columns, from a request (not a saved person). */
const summaryOf = (request) => propertyOrAreasSummary({
  leadType: request.leadType,
  propertyAddress: request.propertyAddress,
  areasOfInterest: request.areasOfInterest,
});

/**
 * Add many people from a CSV or Excel file.
 *
 * choose a file -> preview (what was understood, what is wrong) -> import in batches -> summary.
 * The file is read in the browser; only the rows that passed the checks are sent, 200 at a time, and
 * the service checks every row again. Rows already in the book are skipped, so importing the same
 * file twice (or retrying after an interruption) never creates anyone twice.
 */
const CrmImportDialog = ({ book, ownerUserId, onClose, onImported }) => {
  const [stage, setStage] = useState('choose');          // choose | preview | importing | done
  const [busy, setBusy] = useState(false);
  const [fileError, setFileError] = useState('');
  const [fileName, setFileName] = useState('');
  const [prepared, setPrepared] = useState(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [outcome, setOutcome] = useState({ created: 0, duplicates: 0, skipped: [] });
  const [interrupted, setInterrupted] = useState('');
  const [resumeAt, setResumeAt] = useState(0);

  const ready = prepared ? prepared.entries.filter((e) => e.errors.length === 0) : [];
  const withProblems = prepared ? prepared.entries.filter((e) => e.errors.length > 0) : [];

  const onFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setFileError('');
    setBusy(true);
    try {
      const result = prepareImport(await readSpreadsheet(file));
      if (result.problem) {
        setFileError(result.problem);
      } else {
        setFileName(file.name);
        setPrepared(result);
        setStage('preview');
      }
    } catch (err) {
      setFileError(err.message || 'That file could not be read.');
    } finally {
      setBusy(false);
    }
  };

  const clientSkipped = () => withProblems.map((e) => ({ rowNumber: e.rowNumber, reason: e.errors.join(' '), request: e.request }));

  const run = async (startAt, carry) => {
    const batches = chunk(ready, CHUNK_SIZE);
    let totals = carry;
    setStage('importing');
    setInterrupted('');
    for (let b = startAt; b < batches.length; b++) {
      setResumeAt(b);
      setProgress({ done: b * CHUNK_SIZE, total: ready.length });
      let result;
      try {
        result = await crmService.importPersons(book, batches[b].map((e) => e.request), ownerUserId);
      } catch (err) {
        setOutcome(totals);
        setInterrupted(crmErrorMessage(err));
        return;
      }
      const skipped = [...totals.skipped];
      let { created, duplicates } = totals;
      result.results.forEach((r) => {
        const entry = batches[b][r.index];
        if (r.status === 'CREATED') created++;
        else if (r.status === 'DUPLICATE') {
          duplicates++;
          skipped.push({ rowNumber: entry.rowNumber, reason: 'Already in this book (same email or phone number)', request: entry.request });
        } else {
          skipped.push({ rowNumber: entry.rowNumber, reason: r.message || 'This row could not be imported.', request: entry.request });
        }
      });
      totals = { created, duplicates, skipped };
    }
    setOutcome(totals);
    setProgress({ done: ready.length, total: ready.length });
    setStage('done');
  };

  const start = () => run(0, { created: 0, duplicates: 0, skipped: [] });
  const retry = () => run(resumeAt, outcome);

  const finish = () => {
    if (outcome.created > 0 && onImported) onImported();
    onClose();
  };

  const allSkipped = () => [...clientSkipped(), ...outcome.skipped].sort((a, b) => a.rowNumber - b.rowNumber);

  const body = (
    <div className="crm-modal-backdrop">
      <div className="crm-modal crm-import-modal" role="dialog" aria-modal="true" aria-label="Import people">
        <h2>Import people</h2>

        {stage === 'choose' && (
          <>
            <p className="crm-import-intro">
              Add many people at once from a CSV or Excel (.xlsx) file. The first row must be column headings.
              Up to {MAX_ROWS} people per file.
            </p>
            <ul className="crm-import-tips">
              <li>Include a name, an email or a phone number for each person.</li>
              <li>A lead needs <strong>Buyer</strong>, <strong>Seller</strong> or <strong>Both</strong> in the &ldquo;Buyer or Seller&rdquo; column.</li>
              <li>Sellers: fill in Property Address, City, State and ZIP. Buyers: list their cities under &ldquo;Areas of Interest&rdquo;,
                separated by semicolons, for example <em>Oakland, CA; Berkeley, CA</em>.</li>
              <li>People already in this book (same email or phone) are skipped, so it is safe to import a file twice.</li>
            </ul>
            <button type="button" className="crm-link" onClick={() => download('crm-import-template.csv', templateCsv())}>
              Download a template with examples
            </button>
            <label className="crm-import-file">
              <span className="crm-field-label">Choose a file</span>
              <input type="file" accept=".csv,.xlsx,.txt,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFile} disabled={busy} aria-label="Choose a CSV or Excel file" />
            </label>
            {busy && <p className="crm-note">Reading the file…</p>}
            {fileError && <p className="crm-error" role="alert">{fileError}</p>}
            <div className="crm-modal-actions">
              <button type="button" className="crm-btn" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}

        {stage === 'preview' && prepared && (
          <>
            <p className="crm-import-summary">
              <strong>{fileName}</strong>: {prepared.entries.length} {prepared.entries.length === 1 ? 'person' : 'people'} found.
              {' '}<span className="crm-import-ready">{ready.length} ready to import</span>
              {withProblems.length > 0 && <span className="crm-import-problems">, {withProblems.length} with problems (these will be skipped)</span>}.
            </p>
            <p className="crm-note">
              Using columns: {prepared.recognized.map((r) => r.header).join(', ')}.
              {prepared.ignored.length > 0 && <> Not used: {prepared.ignored.join(', ')}.</>}
            </p>

            {withProblems.length > 0 && (
              <div className="crm-import-problem-list" role="region" aria-label="Rows with problems">
                <strong>Rows with problems</strong>
                <ul>
                  {withProblems.slice(0, 10).map((e) => <li key={e.rowNumber}>Row {e.rowNumber}: {e.errors.join(' ')}</li>)}
                </ul>
                {withProblems.length > 10 && <p>…and {withProblems.length - 10} more. They are all listed in the report you can download afterwards.</p>}
              </div>
            )}

            {ready.length > 0 && (
              <div className="crm-table-wrap">
                <table className="crm-table crm-import-table">
                  <thead><tr><th>Row</th><th>Name</th><th>Buyer / seller</th><th>Property or areas</th><th>Email</th></tr></thead>
                  <tbody>
                    {ready.slice(0, 5).map((e) => (
                      <tr key={e.rowNumber}>
                        <td>{e.rowNumber}</td>
                        <td>{displayName(e.request)}</td>
                        <td>{leadTypeLabel(e.request.leadType)}</td>
                        <td className="crm-wrap">{summaryOf(e.request) || formatAddress(e.request.propertyAddress)}</td>
                        <td>{e.request.emails?.[0]?.value || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {ready.length > 5 && <p className="crm-note">Showing the first 5 of {ready.length}.</p>}
              </div>
            )}

            <div className="crm-modal-actions">
              <button type="button" className="crm-btn" onClick={() => { setPrepared(null); setStage('choose'); }}>Choose another file</button>
              <button type="button" className="crm-btn crm-btn-primary" onClick={start} disabled={ready.length === 0}>
                Import {ready.length} {ready.length === 1 ? 'person' : 'people'}
              </button>
            </div>
          </>
        )}

        {stage === 'importing' && (
          <>
            <p>Importing… {progress.done} of {progress.total}</p>
            <progress className="crm-import-progress" value={progress.done} max={progress.total || 1} aria-label="Import progress" />
            {interrupted && (
              <>
                <p className="crm-error" role="alert">
                  The import was interrupted: {interrupted} {outcome.created} {outcome.created === 1 ? 'person was' : 'people were'} added before it stopped.
                  Trying again is safe: anyone already added is skipped.
                </p>
                <div className="crm-modal-actions">
                  <button type="button" className="crm-btn" onClick={finish}>Close</button>
                  <button type="button" className="crm-btn crm-btn-primary" onClick={retry}>Try again</button>
                </div>
              </>
            )}
          </>
        )}

        {stage === 'done' && (
          <>
            <p className="crm-import-result" role="status">
              <strong>{outcome.created}</strong> {outcome.created === 1 ? 'person' : 'people'} added.
            </p>
            <ul className="crm-import-counts">
              {outcome.duplicates > 0 && <li>{outcome.duplicates} already in this book (skipped)</li>}
              {allSkipped().length - outcome.duplicates > 0 && <li>{allSkipped().length - outcome.duplicates} could not be imported</li>}
            </ul>
            {allSkipped().length > 0 && (
              <button type="button" className="crm-link" onClick={() => download('crm-import-skipped-rows.csv', reportCsv(allSkipped()))}>
                Download the rows that were not imported, with the reason
              </button>
            )}
            <div className="crm-modal-actions">
              <button type="button" className="crm-btn crm-btn-primary" onClick={finish}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(body, document.body);
};

export default CrmImportDialog;
